import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  CreateCustomizationRequest,
  Customization,
  UpdateCustomizationRequest,
} from '@nucleus-commerce/api-client/admin';

import { dictionaryApi } from '../api/dictionary-api';

import { dictionaryKeys } from './dictionary-keys';

function remember(queryClient: ReturnType<typeof useQueryClient>, row: Customization) {
  queryClient.setQueryData<Customization[]>(dictionaryKeys.customizations(), (rows) => {
    if (!rows) return rows;
    const index = rows.findIndex((r) => r.id === row.id);
    const next = index === -1 ? [...rows, row] : rows.with(index, row);
    return next.sort((a, b) => a.position - b.position || a.key.localeCompare(b.key));
  });
  return queryClient.invalidateQueries({ queryKey: dictionaryKeys.customizations() });
}

/** 409 ALREADY_EXISTS on `key`, 422 on a field the input type does not take; the dialog shows them. */
export function useCreateCustomization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCustomizationRequest) => dictionaryApi.createCustomization(body),
    meta: { successMessage: 'dictionary:toasts.created', silentError: true },
    onSuccess: (row) => remember(queryClient, row),
  });
}

export function useUpdateCustomization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCustomizationRequest }) =>
      dictionaryApi.updateCustomization(id, body),
    meta: { successMessage: 'dictionary:toasts.updated', silentError: true },
    onSuccess: (row) => remember(queryClient, row),
  });
}

/** 409 RESOURCE_IN_USE while a product or product type still references it. */
export function useDeleteCustomization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dictionaryApi.removeCustomization(id),
    meta: { successMessage: 'dictionary:toasts.deleted', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dictionaryKeys.customizations() }),
  });
}
